import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Target, TrendingUp, Users, MousePointerClick } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CampaignRoiTab = ({ days = 30 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRoi = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/website-analytics/campaign-roi?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (err) {
        toast.error('Fehler beim Laden der Kampagnen-Daten');
      } finally {
        setLoading(false);
      }
    };
    fetchRoi();
  }, [days]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="roi-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500" />
      </div>
    );
  }

  const campaigns = data?.campaigns || [];
  const named = campaigns.filter((c) => c.campaign);
  const noCampaign = campaigns.find((c) => !c.campaign);

  return (
    <div className="space-y-6" data-testid="campaign-roi-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Target className="w-5 h-5 text-amber-600" />
            Kampagnen-ROI ({days} Tage) — Besucher → Leads → Buchungen
          </CardTitle>
        </CardHeader>
        <CardContent>
          {named.length === 0 ? (
            <div className="text-center py-10 text-gray-500" data-testid="roi-empty">
              <Target className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">Noch keine Kampagnen-Daten im Zeitraum</p>
              <p className="text-sm mt-1">
                Nutze UTM-Parameter (z.B. <code className="bg-gray-100 px-1 rounded">?utm_campaign=eroeffnung_rappi</code>)
                in deinen Anzeigen und Links — die Auswertung erscheint hier automatisch.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="roi-table">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b">
                    <th className="py-2 pr-3">Kampagne</th>
                    <th className="py-2 px-3 text-right">Aufrufe</th>
                    <th className="py-2 px-3 text-right">Besucher</th>
                    <th className="py-2 px-3 text-right">Leads</th>
                    <th className="py-2 px-3 text-right">Buchungen</th>
                    <th className="py-2 px-3 text-right">Besucher→Lead</th>
                    <th className="py-2 pl-3 text-right">Lead→Buchung</th>
                  </tr>
                </thead>
                <tbody>
                  {named.map((c) => (
                    <tr key={c.campaign} className="border-b last:border-0 hover:bg-amber-50/40" data-testid={`roi-row-${c.campaign}`}>
                      <td className="py-2.5 pr-3 font-medium text-gray-900">{c.campaign}</td>
                      <td className="py-2.5 px-3 text-right text-gray-700">{c.pageviews}</td>
                      <td className="py-2.5 px-3 text-right text-gray-700">{c.visitors}</td>
                      <td className="py-2.5 px-3 text-right">
                        <span className="font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{c.leads}</span>
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <span className="font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">{c.bookings}</span>
                      </td>
                      <td className="py-2.5 px-3 text-right text-gray-700">{c.visitor_to_lead}%</td>
                      <td className="py-2.5 pl-3 text-right text-gray-700">{c.lead_to_booking}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Funnel-Karten pro Kampagne */}
      {named.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {named.slice(0, 6).map((c) => (
            <Card key={`funnel-${c.campaign}`} data-testid={`roi-funnel-${c.campaign}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-800">{c.campaign}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <FunnelBar icon={Users} label="Besucher" value={c.visitors} max={c.visitors} color="bg-amber-400" />
                <FunnelBar icon={MousePointerClick} label="Leads" value={c.leads} max={c.visitors} color="bg-blue-500" />
                <FunnelBar icon={TrendingUp} label="Buchungen" value={c.bookings} max={c.visitors} color="bg-emerald-500" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {noCampaign && (
        <p className="text-xs text-gray-400" data-testid="roi-no-campaign-hint">
          Ohne Kampagnen-Zuordnung: {noCampaign.pageviews} Aufrufe, {noCampaign.leads} Leads, {noCampaign.bookings} Buchungen
        </p>
      )}
    </div>
  );
};

const FunnelBar = ({ icon: Icon, label, value, max, color }) => {
  const pct = max > 0 ? Math.max((value / max) * 100, value > 0 ? 4 : 0) : 0;
  return (
    <div className="flex items-center gap-3 text-sm">
      <Icon className="w-4 h-4 text-gray-400 shrink-0" />
      <span className="w-20 text-gray-600 shrink-0">{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-10 text-right font-medium text-gray-900">{value}</span>
    </div>
  );
};

export default CampaignRoiTab;
